import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Button, ButtonGroup, Modal, Form, Alert, Badge, Row, Col } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader.jsx';

const EMPTY = {
  name: '',
  contact_person: '',
  contact_email: '',
  contact_phone: '',
  address_street: '',
  address_zip: '',
  address_city: '',
  address_country: '',
  vat_id: '',
  website: '',
  billing_reference: ''
};

function nullify(obj) {
  // empty strings → null so the DB stays clean
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = (typeof v === 'string' && v.trim() === '') ? null : v;
  }
  return out;
}

export default function CustomersPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [error, setError] = useState(null);

  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.get('/customers', { params: { limit: 1000, include_archived: true } }).then(r => r.data.items)
  });

  const open = (customer) => {
    setEditing(customer || null);
    setDraft(customer ? { ...EMPTY, ...customer } : EMPTY);
    setError(null);
    setShow(true);
  };
  const close = () => { setShow(false); setError(null); };

  const save = useMutation({
    mutationFn: () => {
      const body = nullify(draft);
      return editing
        ? api.patch(`/customers/${editing.id}`, body).then(r => r.data)
        : api.post('/customers', body).then(r => r.data);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['customers'] }); close(); },
    onError: (e) => setError(e.response?.data?.message || (e.response?.data?.details && JSON.stringify(e.response.data.details)) || e.message)
  });

  const archive = useMutation({
    mutationFn: ({ id, archived }) => api.post(`/customers/${id}/${archived ? 'unarchive' : 'archive'}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] })
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/customers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customers'] }),
    onError: (e) => alert(e.response?.data?.message || e.message)
  });

  const set = (k) => (e) => setDraft(d => ({ ...d, [k]: e.target.value }));

  return (
    <>
      <PageHeader title="Kunden" subtitle="Lizenznehmer-Verzeichnis"
        actions={<Button onClick={() => open(null)}><Icon icon="tabler:plus" className="me-1" />Neuer Kunde</Button>} />

      <Card className="card-flat">
        <Card.Body className="p-0">
          {customers.isLoading && <div className="p-4 text-muted">Lade…</div>}
          {customers.data && customers.data.length === 0 && (
            <div className="empty-state">
              <Icon icon="tabler:building-off" className="empty-icon" />
              <div>Noch keine Kunden angelegt.</div>
            </div>
          )}
          {customers.data && customers.data.length > 0 && (
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr><th>Kunde</th><th>Kontakt</th><th>Standort</th><th>Status</th><th style={{ width: 160 }}></th></tr>
              </thead>
              <tbody>
                {customers.data.map(c => (
                  <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/customers/${c.id}`)}>
                    <td>
                      <div className="fw-semibold">
                        <Link to={`/customers/${c.id}`} className="text-decoration-none text-reset"
                          onClick={(e) => e.stopPropagation()}>{c.name}</Link>
                      </div>
                      <div className="small text-muted d-flex gap-2">
                        {c.customer_number && <code>{c.customer_number}</code>}
                        {c.contact_person && <span>· {c.contact_person}</span>}
                      </div>
                    </td>
                    <td className="small">
                      {c.contact_email && <div><Icon icon="tabler:mail" className="me-1" />{c.contact_email}</div>}
                      {c.contact_phone && <div><Icon icon="tabler:phone" className="me-1" />{c.contact_phone}</div>}
                      {!c.contact_email && !c.contact_phone && <span className="text-muted">—</span>}
                    </td>
                    <td className="small">
                      {(c.address_zip || c.address_city) && <div>{[c.address_zip, c.address_city].filter(Boolean).join(' ')}</div>}
                      {c.address_country && <div className="text-muted">{c.address_country}</div>}
                      {!c.address_city && !c.address_country && <span className="text-muted">—</span>}
                    </td>
                    <td>{c.archived_at
                      ? <Badge bg="" className="badge-soft-secondary">Archiviert</Badge>
                      : <Badge bg="" className="badge-soft-success">Aktiv</Badge>}</td>
                    <td className="text-end pe-3" onClick={(e) => e.stopPropagation()}>
                      <ButtonGroup size="sm" className="flex-nowrap">
                        <Button as={Link} to={`/customers/${c.id}`} variant="outline-secondary" title="Anzeigen">
                          <Icon icon="tabler:eye" />
                        </Button>
                        <Button variant="outline-secondary" onClick={() => open(c)} title="Bearbeiten">
                          <Icon icon="tabler:edit" />
                        </Button>
                        <Button variant="outline-secondary"
                          title={c.archived_at ? 'Reaktivieren' : 'Archivieren'}
                          onClick={() => archive.mutate({ id: c.id, archived: !!c.archived_at })}>
                          <Icon icon={c.archived_at ? 'tabler:archive-off' : 'tabler:archive'} />
                        </Button>
                        {c.archived_at && (
                          <Button variant="outline-danger" title="Endgültig löschen"
                            onClick={() => confirm(`Kunde "${c.name}" und alle Anhänge endgültig löschen?`) && del.mutate(c.id)}>
                            <Icon icon="tabler:trash" />
                          </Button>
                        )}
                      </ButtonGroup>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={show} onHide={close} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? `Kunde bearbeiten: ${editing.name}` : 'Neuer Kunde'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

          <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>Stammdaten</h6>
          <Row className="g-3 mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Firmenname *</Form.Label>
                <Form.Control required value={draft.name} onChange={set('name')} placeholder="z.B. ACME GmbH" />
              </Form.Group>
            </Col>
          </Row>
          <Alert variant="info" className="py-2 small mb-3">
            Die Token-Identity (<code>external_id</code>) wird automatisch aus dem als <strong>Primary</strong> markierten
            External-Ref abgeleitet. Externe Verknüpfungen werden im Customer-Detail-View gepflegt.
          </Alert>

          <h6 className="text-uppercase text-muted small mb-3 mt-2" style={{ letterSpacing: '0.05em' }}>Ansprechpartner</h6>
          <Row className="g-3 mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control value={draft.contact_person} onChange={set('contact_person')} placeholder="Max Mustermann" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>E-Mail</Form.Label>
                <Form.Control type="email" value={draft.contact_email} onChange={set('contact_email')} placeholder="kontakt@firma.de" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Telefon</Form.Label>
                <Form.Control value={draft.contact_phone} onChange={set('contact_phone')} placeholder="+49 …" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Website</Form.Label>
                <Form.Control value={draft.website} onChange={set('website')} placeholder="https://" />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="text-uppercase text-muted small mb-3 mt-2" style={{ letterSpacing: '0.05em' }}>Anschrift</h6>
          <Row className="g-3 mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Straße + Hausnummer</Form.Label>
                <Form.Control value={draft.address_street} onChange={set('address_street')} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>PLZ</Form.Label>
                <Form.Control value={draft.address_zip} onChange={set('address_zip')} />
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label>Ort</Form.Label>
                <Form.Control value={draft.address_city} onChange={set('address_city')} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Land</Form.Label>
                <Form.Control value={draft.address_country} onChange={set('address_country')} placeholder="Deutschland" />
              </Form.Group>
            </Col>
          </Row>

          <h6 className="text-uppercase text-muted small mb-3 mt-2" style={{ letterSpacing: '0.05em' }}>Abrechnung</h6>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>USt-ID / VAT-ID</Form.Label>
                <Form.Control value={draft.vat_id} onChange={set('vat_id')} placeholder="DE123456789" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Rechnungsreferenz</Form.Label>
                <Form.Control value={draft.billing_reference} onChange={set('billing_reference')}
                  placeholder="Kundennummer / Vertragsnummer" />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={close}>Abbrechen</Button>
          <Button onClick={() => save.mutate()} disabled={!draft.name || save.isLoading}>
            {save.isLoading ? 'Speichere…' : editing ? 'Änderungen speichern' : 'Anlegen'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
